import { useQuery } from '@tanstack/react-query'
import { portalLanguagesData } from 'app/appSettings'
import { queryGetObject, queryGetObjects } from 'app/backend/Common'
import { FormPage } from 'app/backend/salesforce-maps/FormPage'
import { SurveyTranslation } from 'app/backend/salesforce-maps/SurveyTranslation'
import { transformOldSfProps } from 'app/utils/transformOldSfProps'
import { isNil } from 'lodash'
import pako from 'pako'
import { isValidJSON } from 'utils'
import { textFields } from '../sfData/sfSurvey'

interface UseFormProps {
  formId?: string
}

interface HookData extends Partial<FormPage> {
  title: {
    portalTranslationId?: {
      id: string | null
      text: string
    }
  }
  translations: SurveyTranslation | undefined
}

interface UseFormResult {
  data: HookData | undefined
  isLoading: boolean
  error: unknown
  refetch: () => Promise<RefetchAllResult>
}

interface ProcessedFormData extends HookData {
  holdsReusableElements?: boolean
  objectsConnected?: FormPage['objectsConnected']
  comments?: string
  origin?: string
  type?: string
  status?: string
  id?: string
}

interface RefetchAllResult {
  data: ProcessedFormData | undefined
  error: unknown
}

/**
 * Hook for fetching form data
 */
export const useForm = (props: UseFormProps): UseFormResult => {
  const {
    formId: propFormId
  } = props

  // Get the form configuration if formId is not provided
  const {
    data: formConfig,
    error: formConfigError,
    isLoading: isLoadingFormConfig,
    refetch: refetchFormConfig
  } = useQuery({
    queryKey: ['configuration', 'FORM_ORGANIZATION_DETAILS'],
    queryFn: async () => {
      const result = await queryGetObjects({
        type: 'Configuration',
        searchProps: {
          Name: 'FORM_ORGANIZATION_DETAILS'
        }
      })
      return result[0]
    },
    enabled: propFormId === undefined
  })

  const effectiveFormId = propFormId ?? formConfig?.value

  const {
    data: form,
    error: formError,
    isLoading: isLoadingForm,
    refetch: refetchForm
  } = useQuery({
    queryKey: ['form', effectiveFormId],
    queryFn: async () => {
      const result = await queryGetObject({
        type: 'FormPage',
        id: effectiveFormId ?? ''
      })
      return result
    },
    enabled: Boolean(effectiveFormId)
  })

  const titleTranslation = form?.titleTranslation

  const surveyTranslationQuery = useQuery({
    queryKey: ['survey-translation', titleTranslation],
    queryFn: async () => {
      const result = await queryGetObject({
        type: 'SurveyTranslation',
        id: titleTranslation ?? '',
        include: [{ base: 'Survey_Texts__r', select: textFields }]
      })
      return result
    },
    enabled: Boolean(titleTranslation)
  })

  const processFormData = (
    formData: any,
    translationsData: any
  ): ProcessedFormData | undefined => {
    const title = isNil(translationsData)
      ? Object.values(portalLanguagesData).reduce<{
        portalTranslationId?: { id: string | null, text: string }
      }>((acc, langObj) => {
        const { editKey } = langObj
        acc[editKey] = {
          id: null,
          text: ''
        }
        return acc
      }, {})
      : Object.values(portalLanguagesData).reduce<{
        portalTranslationId?: { id: string | null, text: string }
      }>((acc, langObj) => {
        const { editKey, surveyTextKey } = langObj
        const translation =
            translationsData?.surveyTexts?.records.find((rec) =>
              rec.Language__c.includes(surveyTextKey)
            )
        acc[editKey] = {
          id: translation?.Id ?? null,
          text: translation?.Text__c ?? ''
        }
        return acc
      }, {})

  
  const portalTranslation =
      translationsData?.surveyTexts?.records.find(
        (rec) => rec.Text_Is_Portal_Translation_Id__c
      )

    title.portalTranslationId = {
      id: portalTranslation?.Id ?? null,
      text: portalTranslation?.Text__c ?? ''
    }

    let toPass: FormPage | undefined

    if (isValidJSON(formData?.data)) {
      toPass = JSON.parse(typeof formData?.data === 'string' ? formData.data : '') as FormPage
    } else if (typeof formData?.data === 'string') {
      try {
        const gzip = Buffer.from(formData.data ?? '', 'base64')
        const unzipped = pako.ungzip(gzip, { to: 'string' })
        const parsed = JSON.parse(unzipped) as FormPage
        toPass = transformOldSfProps(parsed) as FormPage
      } catch (error) {
        console.log(error)
      }
    }

    return formData != null
      ? {
          ...toPass,
          title,
          translations: translationsData,
          holdsReusableElements: formData.holdsReusableElements,
          objectsConnected: toPass?.objectsConnected,
          comments: formData.comments,
          origin: formData.origin,
          type: formData.type,
          status: formData.status,
          id: formData.id
        }
      : undefined
  }

  const refetchAll = async (): Promise<RefetchAllResult> => {
    const configResult = await refetchFormConfig()
    const formResult = await refetchForm()

  const refreshedForm = formResult.data

  let refreshedTranslations
    if (refreshedForm?.titleTranslation != null) {
      const translationsResult = await surveyTranslationQuery.refetch()
      refreshedTranslations = translationsResult.data
    }

    return {
      data: processFormData(refreshedForm, refreshedTranslations),
      error: formResult.error ?? configResult.error
    }
  }

  const translations = surveyTranslationQuery.data
  const data = processFormData(form, translations)

  return {
    data,
    isLoading: Boolean(isLoadingForm) || isLoadingFormConfig,
    error: formError ?? formConfigError,
    refetch: refetchAll
  }
}
